import { Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import privacyEn from "../../../../../content/legal/privacy-en.md";
import privacyEs from "../../../../../content/legal/privacy-es.md";
import privacyFr from "../../../../../content/legal/privacy-fr.md";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import LogoHeader from "../../components/app/headers/LogoHeader";
import Navbar from "../../components/app/navbars/Navbar";
import ContentMarkdown from "../../components/ContentMarkdown";
import Loader from "../../components/Loader";
import { PolicyTextStyle } from "../../components/PolicyTextStyle";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { loadMarkdown } from "../../domains/app/utils/loadMarkdown";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { useFirebaseUser } from "../../domains/user/hooks/useFirebaseUser";
import { PartnersList } from "../../components/partners/PartnersList";

export default function PolicyPage() {
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const { isAuthenticatedFirebase: isAuthenticated } = useFirebaseUser();
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const searchParams = new URLSearchParams(history.location.search);
  const hideTitle = searchParams.get("hideTitle") !== null;
  const isPush = searchParams.get("isPush") !== null;
  const activeTab = searchParams.get("tab") === "partners" ? 1 : 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number): void => {
    const newSearchParams = new URLSearchParams(history.location.search);
    newSearchParams.set("tab", newValue === 1 ? "partners" : "juisci");
    history.replace({
      pathname: history.location.pathname,
      search: newSearchParams.toString(),
    });
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        if (isIOSApp()) iosSetBackgroundColor("#FFFFFF");

        // French and Spanish have their own text; every other language reads
        // the English version.
        const privacyByLang: Partial<
          Record<typeof activeLang, typeof privacyEn>
        > = { es: privacyEs, fr: privacyFr };
        const md = privacyByLang[activeLang] ?? privacyEn;

        setMarkdownContent(await loadMarkdown(md));
      } catch (error) {
        console.error("Couldn't load file.", error);
        displayToast(t("Something went wrong. Please try again."), "error");
      }
    };

    initialize();
  }, [activeLang, t]);

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        isPush ? (
          <ContentHeader
            transparent
            title={hideTitle ? undefined : t("Privacy Policy")}
          />
        ) : (
          <LogoHeader />
        )
      }
      footer={!isPush && <Navbar />}
    >
      {isAuthenticated ? (
        <Tabs
          variant="fullWidth"
          centered
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            "& .MuiButtonBase-root": {
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "120%",
              letterSpacing: "5e-5em",
              textTransform: "capitalize",
              color: "var(--color-secondary-bluegrey-4)",
              padding: "16px",
              borderBottom: "0.5px solid #90a4ae",
              "&.Mui-selected": { color: "var(--color-primary-black)" },
            },
            "& .MuiTabs-indicator": {
              height: "1.5px",
              backgroundColor: "var(--color-primary-black)",
            },
          }}
        >
          <Tab label="Juisci" />
          <Tab label="Partners" />
        </Tabs>
      ) : (
        <div />
      )}
      <div className="p-6">
        <div style={{ display: activeTab === 0 ? "inherit" : "none" }}>
          {markdownContent ? (
            <>
              <PolicyTextStyle>
                <ContentMarkdown math={false} linkVariant="anchor">
                  {markdownContent}
                </ContentMarkdown>
              </PolicyTextStyle>

              <hr style={{ margin: "30px 0" }} />

              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => {
                  history.replace("/terms?isPush=true");
                }}
              >
                {t("See our Terms of Use")}
              </Button>
            </>
          ) : (
            <Loader />
          )}
        </div>

        {isAuthenticated ? (
          <div style={{ display: activeTab === 1 ? "inherit" : "none" }}>
            {/* The choice lives in "Partner sharing" (Figma O4): the
                Partners tab only lists the partners. */}
            <PartnersList layout="mobile" policies={null} />
          </div>
        ) : (
          <div />
        )}
      </div>
    </ScrollableLayout>
  );
}
